import { Box, CircularProgress } from '@material-ui/core';
import { DocumentStatus, PaymentMethodType } from '@nemuru-eng/js-api-client';
import { useState } from 'react';

import { useWidth } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/_shared/hooks/useWidth';
import { CustomRequiredFile } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/_shared/types';
import { UploadFileManager } from '@/components/modules/Documentation/components/DocumentationDetails/components/UploadFileManager/UploadFileManager';
import { useRequiredFile } from '@/components/modules/Documentation/components/DocumentationDetails/hooks/useRequiredFile';
import { DocumentDetailsStandardAction } from '@/components/modules/Documentation/components/DocumentationHeader/types/DocumentDetailsStandardAction';
import { DocumentationMode } from '@/components/modules/Documentation/types/DocumentationMode';
import { CustomPaper } from '@/components/shared/components/CustomPaper';
import { FlexRow } from '@/components/shared/components/FlexRow';
import { Font } from '@/components/shared/components/Font';
import { Icon } from '@/components/shared/components/Icon';
import { StandardButton } from '@/components/shared/components/StandardButton';
import { Text } from '@/components/shared/components/Text';
import { useCustomTheme } from '@/hooks/useCustomTheme';

interface UploadFileCardProps {
  requiredFile: CustomRequiredFile;
  paymentMethodId: string;
  paymentMethodType: PaymentMethodType;
  borrowerId: string;
  onUploadSuccess: () => Promise<void>;
}

export const UploadFileCard = ({
  requiredFile,
  paymentMethodId,
  paymentMethodType,
  borrowerId,
  onUploadSuccess,
}: UploadFileCardProps) => {
  const { palette } = useCustomTheme();

  const { ref, width } = useWidth<HTMLDivElement>();

  const [isRefreshing, setIsRefreshing] = useState(false);

  const { uploadFile, resetUseRequiredFileState, openActionDialogByActionType, opened, setOpened } = useRequiredFile({
    requiredFile,
    documentationRequiredType: requiredFile.type,
    documentationRequiredId: requiredFile.documentationRequiredId,
    paymentMethodId,
    paymentMethodType,
    borrowerId,
    mode: DocumentationMode.UPLOAD_ONLY,
  });

  const hasUploadedFiles = requiredFile.uploadedFiles?.length && requiredFile.uploadedFiles?.length > 0;
  const hasIncidence = requiredFile.status === DocumentStatus.INCIDENCE;

  const onUploadCompleted = async () => {
    setIsRefreshing(true);
    try {
      await onUploadSuccess();
    } catch (error) {
      console.error(error);
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <>
      <CustomPaper
        fill={hasIncidence ? 'warning.light' : hasUploadedFiles ? 'success.light' : 'grey.light'}
        style={{ padding: 16 }}
      >
        <div ref={ref}>
          <FlexRow
            gridGap={16}
            flexDirection={width && width < 400 ? 'column' : 'row'}
            alignItems={width && width < 400 ? 'flex-start' : 'center'}
          >
            <FlexRow alignItems={'flex-start'} justifyContent={'flex-start'} gridGap={8}>
              {hasIncidence ? (
                <Icon size={16} color={palette.warning.main}>
                  error_outline
                </Icon>
              ) : hasUploadedFiles ? (
                <Icon size={16} color={palette.success.main}>
                  check
                </Icon>
              ) : (
                <Icon size={16} color={palette.text.secondary}>
                  hourglass_empty
                </Icon>
              )}
              <Box>
                <FlexRow gridGap={8} justifyContent={'flex-start'}>
                  <Font variant="body2" color="title">
                    <Text strong tId={requiredFile.name} />
                  </Font>
                </FlexRow>
                <Font variant="body2" color="textPrimary" className="line-clamp-2">
                  {requiredFile.description}
                </Font>
                {requiredFile.annotation && hasIncidence && (
                  <Font
                    variant="body2"
                    style={{
                      color: palette.error.main,
                    }}
                  >
                    {requiredFile.annotation}
                  </Font>
                )}
              </Box>
            </FlexRow>
            <Box flexShrink={0}>
              {isRefreshing ? (
                <CircularProgress size={18} style={{ color: palette.secondary.main, marginRight: 12 }} />
              ) : (
                <StandardButton
                  size="small"
                  variant="text"
                  color="secondary"
                  onClick={() => openActionDialogByActionType(DocumentDetailsStandardAction.UPLOAD_FILE)}
                >
                  <Text tId={'UPLOAD'} />
                  <Icon size={20} style={{ marginLeft: 8 }}>
                    {'upload'}
                  </Icon>
                </StandardButton>
              )}
            </Box>
          </FlexRow>
        </div>
      </CustomPaper>
      {uploadFile && opened && (
        <UploadFileManager
          loanId={paymentMethodId}
          requiredFile={requiredFile}
          opened={opened}
          setOpened={setOpened}
          setActionDialogParams={resetUseRequiredFileState}
          documentationRequiredId={requiredFile.documentationRequiredId}
          documentationRequiredType={requiredFile.type}
          onUploadSuccess={onUploadCompleted}
        />
      )}
    </>
  );
};
